import { openSafeExternalUrl } from '../../utils/safeNavigation';
import { friendlyError } from '../../utils/userMessages';
// src/components/admin/AdminSupport.jsx
// ─────────────────────────────────────────────────────────────────────────────
//  TelcoFair · Admin Support (Liquid Glassmorphism)
//  "TelcoFair Glass Workspace" — same system as Companies.jsx / Dashboard.jsx
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Paper,
    Typography,
    Grid,
    Avatar,
    Chip,
    IconButton,
    TextField,
    Button,
    Divider,
    Badge,
    Tooltip,
    ListItemAvatar,
    ListItemText,
    ListItemButton,
    InputAdornment,
    CircularProgress,
    useTheme,
    useMediaQuery,
    Menu,
    MenuItem,
    Dialog,
    DialogContent,
    DialogActions,
    LinearProgress,
    Alert,
    Collapse,
    Rating,
    Card,
    CardContent,
    Stack,
    Skeleton,
    Switch,
    FormControlLabel,
    Select,
    FormControl,
    InputLabel,
    MenuItem as MuiMenuItem,
    Snackbar,
    Fade,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import {
    Send as SendIcon,
    AttachFile as AttachFileIcon,
    EmojiEmotions as EmojiIcon,
    Search as SearchIcon,
    Close as CloseIcon,
    CheckCircle as CheckCircleIcon,
    SupportAgent as SupportAgentIcon,
    Message as MessageIcon,
    Person as PersonIcon,
    Business as BusinessIcon,
    Verified as VerifiedIcon,
    QuestionAnswer as QuestionAnswerIcon,
    Quickreply as ReplyIcon,
    Warning as ReportIcon,
    Refresh as RefreshIcon,
    Download as DownloadIcon,
    Description as ContentCopyIcon,
    InsertDriveFile as FileIcon,
    Quickreply as QuickReplyIcon,
    Info as InfoIcon,
    Flag as FlagIcon,
    Timer as TimerIcon,
    Assignment as AssignmentIcon,
    Check as CheckIcon,
    Warning as WarningIcon,
    FiberManualRecord as LiveIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    Chat as ChatIcon,
    VolumeUp as VolumeUpIcon,
    VolumeOff as VolumeOffIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../config/firebase';
import {
    collection,
    onSnapshot,
    addDoc,
    updateDoc,
    doc,
    getDocs,
    serverTimestamp,
    writeBatch,
    setDoc,
} from 'firebase/firestore';
import toast from 'react-hot-toast';
import { uploadAdminFile } from '../../services/firebase/storage';

/* ═══════════════════════════════════════════════════════════════════════════
   1 · DESIGN TOKENS
   ═══════════════════════════════════════════════════════════════════════════ */

const palette = {
    canvas: '#F2F7F9',
    canvasDeep: '#E7F0F4',
    paper: '#FFFFFF',
    ink: '#182235',
    inkSoft: '#506175',
    inkLight: '#7B8B9C',
    inkFaint: '#AAB6C1',
    hairline: 'rgba(31, 62, 82, 0.09)',
    hairlineSoft: 'rgba(31, 62, 82, 0.055)',

    rust: 'var(--primary, #2E6F8E)',
    rustDeep: 'var(--secondary, #42B89B)',
    rustGlow: 'var(--primary, #2E6F8E)',

    amber: '#D78B28',
    amberGlow: '#F1B75F',

    pine: '#2E8B69',
    pineGlow: '#69B99A',

    teal: '#268AA3',
    tealGlow: '#64B8C9',

    plum: '#8D5AC8',
    plumGlow: '#B78BDF',

    indigo: '#596BCE',
    indigoGlow: '#8796E2',

    success: '#1C8A56',
    warning: '#D18A0E',
    danger: '#C9342B',
    coral: '#E87461',
    sage: '#8BA888',
    lavender: '#9B7BB8',
};

const ACCENTS = [palette.rust, palette.teal, palette.amber, palette.pine, palette.plum, palette.indigo, palette.coral, palette.sage, palette.lavender];

const fontDisplay = "'Plus Jakarta Sans', 'Inter', sans-serif";
const fontBody = "'Plus Jakarta Sans', 'Inter', sans-serif";
const fontMono = "'JetBrains Mono', 'SFMono-Regular', monospace";

const FONT_IMPORT = `@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');`;

/* ═══════════════════════════════════════════════════════════════════════════
   2 · HELPERS
   ═══════════════════════════════════════════════════════════════════════════ */

const hexToRgb = (hex) => {
    if (typeof hex !== 'string') return { r: 22, g: 18, b: 16 };
    let h = hex.replace('#', '').trim();
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    if (h.length !== 6 || /[^0-9a-f]/i.test(h)) return { r: 22, g: 18, b: 16 };
    return {
        r: parseInt(h.slice(0, 2), 16),
        g: parseInt(h.slice(2, 4), 16),
        b: parseInt(h.slice(4, 6), 16),
    };
};

const rgba = (color, a = 1) => {
    if (typeof color === 'string' && color.startsWith('var(')) return `color-mix(in srgb, ${color} ${Math.max(0, Math.min(100, Number(a) * 100))}%, transparent)`;
    if (typeof color === 'string' && color.startsWith('rgb')) return color;
    const { r, g, b } = hexToRgb(color);
    return `rgba(${r}, ${g}, ${b}, ${a})`;
};

const liquid = (accent = palette.rust, { depth = 1, radius = 26 } = {}) => ({
    position: 'relative',
    borderRadius: `${radius}px`,
    background: `linear-gradient(155deg, ${rgba('#FFFFFF', 0.82)} 0%, ${rgba('#FFFFFF', 0.55)} 42%, ${rgba(accent, 0.07)} 100%)`,
    backdropFilter: 'blur(22px) saturate(180%)',
    WebkitBackdropFilter: 'blur(22px) saturate(180%)',
    border: `1px solid ${rgba('#FFFFFF', 0.6)}`,
    boxShadow: [
        `inset 0 1px 0 ${rgba('#FFFFFF', 0.95)}`,
        `inset 0 -1px 0 ${rgba('#FFFFFF', 0.35)}`,
        `inset 0 0 0 1px ${rgba(accent, 0.06)}`,
        `0 ${2 * depth}px ${6 * depth}px ${rgba(palette.ink, 0.04)}`,
        `0 ${10 * depth}px ${30 * depth}px -${8 * depth}px ${rgba(accent, 0.16)}`,
    ].join(', '),
    overflow: 'hidden',
    transition: 'box-shadow .5s cubic-bezier(.22,1,.36,1), transform .5s cubic-bezier(.22,1,.36,1), border-color .4s ease',
});

const glassField = (accent = palette.rust) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: '14px',
        fontSize: '0.875rem',
        background: rgba('#FFFFFF', 0.6),
        backdropFilter: 'blur(8px)',
        transition: 'box-shadow .3s ease, transform .3s ease',
        '& fieldset': { borderColor: palette.hairline, borderWidth: '1.5px' },
        '&:hover fieldset': { borderColor: rgba(accent, 0.4) },
        '&.Mui-focused fieldset': { borderColor: accent, borderWidth: '1.5px' },
        '&.Mui-focused': {
            boxShadow: `0 0 0 4px ${rgba(accent, 0.12)}`,
            transform: 'translateY(-2px)',
        },
        '&:hover': {
            transform: 'translateY(-1px)',
        },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: accent },
});

const useSheen = () => {
    const ref = useRef(null);
    const onPointerMove = useCallback((e) => {
        const el = ref.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        el.style.setProperty('--mx', `${((e.clientX - r.left) / r.width) * 100}%`);
        el.style.setProperty('--my', `${((e.clientY - r.top) / r.height) * 100}%`);
        el.style.setProperty('--sheen', '1');
    }, []);
    const onPointerLeave = useCallback(() => {
        const el = ref.current;
        if (el) el.style.setProperty('--sheen', '0');
    }, []);
    return { ref, onPointerMove, onPointerLeave };
};

const sheenLayer = (accent) => ({
    content: '""',
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    opacity: 'var(--sheen, 0)',
    transition: 'opacity .45s ease',
    background: `radial-gradient(420px circle at var(--mx, 50%) var(--my, 0%), ${rgba('#FFFFFF', 0.75)}, ${rgba(accent, 0.10)} 38%, transparent 68%)`,
    mixBlendMode: 'soft-light',
});

const compact = (n) => {
    const v = Number(n) || 0;
    if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
    if (Math.abs(v) >= 10_000) return `${(v / 1000).toFixed(1)}K`;
    return Math.round(v).toLocaleString();
};

/* ═══════════════════════════════════════════════════════════════════════════
   3 · LIQUID BACKDROP
   ═══════════════════════════════════════════════════════════════════════════ */

const LiquidBackdrop = React.memo(function LiquidBackdrop() {
    const motes = useMemo(
        () =>
            Array.from({ length: 25 }, (_, i) => ({
                id: i,
                size: 2 + ((i * 3) % 6),
                x: (i * 37) % 100,
                y: (i * 53) % 100,
                duration: 20 + ((i * 4) % 15),
                delay: (i * 2.1) % 12,
                color: ACCENTS[i % ACCENTS.length],
            })),
        []
    );

    return (
        <Box sx={{ position: 'fixed', inset: 0, zIndex: 0, overflow: 'hidden', pointerEvents: 'none' }}>
            <Box component="svg" sx={{ position: 'absolute', width: 0, height: 0 }} aria-hidden>
                <defs>
                    <filter id="tf-support-liquid">
                        <feTurbulence type="fractalNoise" baseFrequency="0.008 0.012" numOctaves="2" seed="7" result="noise">
                            <animate attributeName="baseFrequency" dur="35s" values="0.008 0.012;0.014 0.006;0.008 0.012" repeatCount="indefinite" />
                        </feTurbulence>
                        <feDisplacementMap in="SourceGraphic" in2="noise" scale="55" xChannelSelector="R" yChannelSelector="G" />
                    </filter>
                </defs>
            </Box>

            <motion.div
                animate={{ x: [0, -50, 35, 0], y: [0, 35, -25, 0], scale: [1, 1.08, 0.95, 1] }}
                transition={{ duration: 28, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                    position: 'absolute', top: '-10%', left: '-3%',
                    width: 550, height: 550, borderRadius: '50%',
                    background: `radial-gradient(circle at 40% 40%, ${rgba(palette.rustGlow, 0.2)}, ${rgba(palette.rust, 0.08)} 48%, transparent 72%)`,
                    filter: 'url(#tf-support-liquid) blur(50px)',
                }}
            />
            <motion.div
                animate={{ x: [0, 55, -30, 0], y: [0, -35, 25, 0], scale: [1, 0.92, 1.08, 1] }}
                transition={{ duration: 33, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                    position: 'absolute', bottom: '-8%', right: '-4%',
                    width: 500, height: 500, borderRadius: '50%',
                    background: `radial-gradient(circle at 55% 45%, ${rgba(palette.plumGlow, 0.18)}, ${rgba(palette.teal, 0.08)} 50%, transparent 72%)`,
                    filter: 'url(#tf-support-liquid) blur(50px)',
                }}
            />
            <motion.div
                animate={{ x: ['-50%', '-54%', '-46%', '-50%'], y: ['-50%', '-46%', '-54%', '-50%'], scale: [1, 1.05, 0.97, 1] }}
                transition={{ duration: 30, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                    position: 'absolute', top: '45%', left: '50%',
                    width: 650, height: 650, borderRadius: '50%',
                    background: `radial-gradient(circle, ${rgba(palette.amberGlow, 0.12)}, transparent 65%)`,
                    filter: 'blur(70px)',
                }}
            />

            {motes.map((m) => (
                <motion.span
                    key={m.id}
                    initial={{ opacity: 0 }}
                    animate={{
                        y: [`${m.y}%`, `${m.y - 35}%`, `${m.y}%`],
                        x: [`${m.x}%`, `${m.x + 10}%`, `${m.x}%`],
                        opacity: [0, 0.5, 0],
                        scale: [0.4, 1.2, 0.4],
                    }}
                    transition={{ duration: m.duration, delay: m.delay, repeat: Infinity, ease: 'easeInOut' }}
                    style={{
                        position: 'absolute', top: 0, left: 0,
                        width: m.size, height: m.size, borderRadius: '50%',
                        background: `radial-gradient(circle at 30% 30%, #fff, ${m.color})`,
                        boxShadow: `0 0 12px ${rgba(m.color, 0.4)}`,
                    }}
                />
            ))}

            <Box
                sx={{
                    position: 'absolute', inset: 0, opacity: 0.3,
                    backgroundImage: `linear-gradient(${rgba(palette.ink, 0.02)} 1px, transparent 1px),
                                      linear-gradient(90deg, ${rgba(palette.ink, 0.02)} 1px, transparent 1px)`,
                    backgroundSize: '60px 60px',
                    maskImage: 'radial-gradient(circle at 50% 30%, #000 0%, transparent 75%)',
                    WebkitMaskImage: 'radial-gradient(circle at 50% 30%, #000 0%, transparent 75%)',
                }}
            />
        </Box>
    );
});

/* ═══════════════════════════════════════════════════════════════════════════
   4 · GLASS PRIMITIVES
   ═══════════════════════════════════════════════════════════════════════════ */

const GlassIcon = ({ icon, color, size = 42 }) => (
    <Box
        sx={{
            width: size, height: size,
            borderRadius: `${size * 0.34}px`,
            display: 'grid',
            placeItems: 'center',
            color: '#fff',
            background: `linear-gradient(150deg, ${rgba(color, 0.95)}, ${rgba(color, 0.72)})`,
            boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.55)}, inset 0 -2px 6px ${rgba(palette.ink, 0.18)}, 0 8px 20px -6px ${rgba(color, 0.55)}`,
            '& svg': { fontSize: size * 0.48, filter: 'drop-shadow(0 1px 1px rgba(0,0,0,.18))' },
        }}
    >
        {icon}
    </Box>
);

const GlassPanel = ({
    accent = palette.rust,
    delay = 0,
    lift = false,
    padding = { xs: 2.25, md: 2.75 },
    children,
    sx = {},
    ...rest
}) => {
    const { ref, onPointerMove, onPointerLeave } = useSheen();
    const [isHovered, setIsHovered] = useState(false);

    return (
        <motion.div
            initial={{ opacity: 0, y: 22, filter: 'blur(6px)' }}
            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
            style={{ height: '100%', width: '100%', minWidth: 0 }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <Paper
                ref={ref}
                onPointerMove={onPointerMove}
                onPointerLeave={onPointerLeave}
                elevation={0}
                sx={{
                    ...liquid(accent),
                    p: padding,
                    height: '100%',
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    boxSizing: 'border-box',
                    '&::after': sheenLayer(accent),
                    transform: isHovered ? 'translateY(-4px) scale(1.005)' : 'translateY(0) scale(1)',
                    transition: 'box-shadow .6s cubic-bezier(.22,1,.36,1), transform .6s cubic-bezier(.22,1,.36,1)',
                    ...(lift && {
                        '&:hover': {
                            boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 1)}, 0 26px 56px -18px ${rgba(accent, 0.34)}`,
                        },
                    }),
                    ...sx,
                }}
                {...rest}
            >
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                    {children}
                </Box>
            </Paper>
        </motion.div>
    );
};

const PanelHead = ({ icon, title, subtitle, accent = palette.rust, chip, right, dense = false }) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            flexWrap: 'wrap',
            mb: dense ? 1.75 : 2.5,
        }}
    >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            {icon && <GlassIcon icon={icon} color={accent} size={dense ? 36 : 42} />}
            <Box sx={{ minWidth: 0 }}>
                <Typography
                    sx={{
                        fontFamily: fontDisplay,
                        fontWeight: 600,
                        fontSize: dense ? '1.02rem' : '1.14rem',
                        color: palette.ink,
                        letterSpacing: '-0.015em',
                        lineHeight: 1.15,
                    }}
                >
                    {title}
                </Typography>
                {subtitle ? (
                    <Typography sx={{ fontSize: '0.76rem', color: palette.inkLight, mt: 0.25 }}>{subtitle}</Typography>
                ) : (
                    <Box sx={{ width: dense ? 34 : 44, height: 3, mt: 0.75, borderRadius: 3, background: `linear-gradient(90deg, ${accent}, ${rgba(accent, 0)})` }} />
                )}
            </Box>
            {chip && (
                <Chip
                    label={chip}
                    size="small"
                    sx={{
                        height: 24,
                        fontFamily: fontMono,
                        fontSize: '0.62rem',
                        fontWeight: 600,
                        letterSpacing: '0.04em',
                        textTransform: 'uppercase',
                        color: accent,
                        bgcolor: rgba(accent, 0.09),
                        border: `1px solid ${rgba(accent, 0.16)}`,
                    }}
                />
            )}
        </Box>
        {right}
    </Box>
);

const StatCard = ({ label, value, icon, accent, delta, hint, delay = 0, loading }) => {
    const { ref, onPointerMove, onPointerLeave } = useSheen();
    const displayValue = typeof value === 'number' ? compact(value) : value;

    return (
        <motion.div
            key={label}
            initial={{ opacity: 0, y: 24, filter: 'blur(8px)' }}
            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
            style={{ height: '100%', minWidth: 0 }}
        >
            <Paper
                ref={ref}
                onPointerMove={onPointerMove}
                onPointerLeave={onPointerLeave}
                elevation={0}
                sx={{
                    ...liquid(accent, { radius: 24 }),
                    height: '100%',
                    p: { xs: 2, md: 2.4 },
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 0,
                    '&::after': sheenLayer(accent),
                    '&:hover': {
                        transform: 'translateY(-7px) scale(1.012)',
                        boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 1)}, 0 28px 60px -20px ${rgba(accent, 0.42)}`,
                    },
                    '&:hover .tf-orb': { opacity: 0.4, transform: 'scale(1.25)' },
                }}
            >
                <Box
                    className="tf-orb"
                    sx={{
                        position: 'absolute',
                        top: -70,
                        right: -70,
                        width: 170,
                        height: 170,
                        borderRadius: '50%',
                        filter: 'blur(38px)',
                        opacity: 0.16,
                        background: `radial-gradient(circle, ${accent}, transparent 68%)`,
                        transition: 'opacity .6s ease, transform .6s cubic-bezier(.22,1,.36,1)',
                        pointerEvents: 'none',
                    }}
                />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1.75 }}>
                        <GlassIcon icon={icon} color={accent} size={40} />
                        {delta && (
                            <Chip
                                size="small"
                                icon={delta.trend === 'down' ? <TrendingDownIcon sx={{ fontSize: 13 }} /> : <TrendingUpIcon sx={{ fontSize: 13 }} />}
                                label={delta.label}
                                sx={{
                                    height: 22,
                                    fontFamily: fontMono,
                                    fontSize: '0.6rem',
                                    fontWeight: 700,
                                    color: delta.trend === 'up' ? palette.success : palette.danger,
                                    bgcolor: rgba(delta.trend === 'up' ? palette.success : palette.danger, 0.09),
                                    border: `1px solid ${rgba(delta.trend === 'up' ? palette.success : palette.danger, 0.18)}`,
                                    '& .MuiChip-icon': { color: delta.trend === 'up' ? palette.success : palette.danger, ml: '6px' },
                                }}
                            />
                        )}
                    </Box>

                    {loading ? (
                        <Skeleton width="60%" height={40} sx={{ bgcolor: palette.hairline }} />
                    ) : (
                        <Typography
                            sx={{
                                fontFamily: fontDisplay,
                                fontWeight: 600,
                                fontSize: { xs: '1.85rem', md: '2.2rem' },
                                lineHeight: 1,
                                letterSpacing: '-0.03em',
                                background: `linear-gradient(140deg, ${palette.ink} 20%, ${accent})`,
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                fontVariantNumeric: 'tabular-nums',
                            }}
                        >
                            {displayValue}
                        </Typography>
                    )}

                    <Typography
                        sx={{
                            mt: 0.9,
                            fontFamily: fontMono,
                            fontSize: '0.62rem',
                            fontWeight: 500,
                            textTransform: 'uppercase',
                            letterSpacing: '0.12em',
                            color: palette.inkSoft,
                        }}
                    >
                        {label}
                    </Typography>
                    {hint && (
                        <Typography sx={{ mt: 'auto', pt: 1.5, fontSize: '0.68rem', color: palette.inkLight, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Box
                                component="span"
                                sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    bgcolor: accent,
                                    boxShadow: `0 0 0 3px ${rgba(accent, 0.16)}`,
                                    animation: 'tfPulse 2.4s ease-in-out infinite',
                                }}
                            />
                            {hint}
                        </Typography>
                    )}
                </Box>
            </Paper>
        </motion.div>
    );
};

const MetaPill = ({ label, tone = palette.inkLight, pulse = false, icon }) => (
    <Box
        sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.55,
            px: 1,
            py: 0.35,
            borderRadius: '9px',
            whiteSpace: 'nowrap',
            fontFamily: fontMono,
            fontSize: '0.6rem',
            fontWeight: 600,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: tone,
            bgcolor: rgba(tone, 0.1),
            border: `1px solid ${rgba(tone, 0.2)}`,
        }}
    >
        {icon
            ? React.cloneElement(icon, { sx: { fontSize: 12 } })
            : <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: tone, animation: pulse ? 'tfPulse 2.4s ease-in-out infinite' : 'none' }} />}
        {label}
    </Box>
);

const GlassInput = ({ label, icon, color = palette.rust, ...props }) => {
    const [focused, setFocused] = useState(false);
    const [hovered, setHovered] = useState(false);

    return (
        <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
            <TextField
                fullWidth
                label={label}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '14px',
                        fontSize: '0.875rem',
                        background: focused ? rgba('#FFFFFF', 0.85) : hovered ? rgba('#FFFFFF', 0.65) : rgba('#FFFFFF', 0.45),
                        backdropFilter: 'blur(12px)',
                        transition: 'all 0.4s cubic-bezier(0.22, 1, 0.36, 1)',
                        '& fieldset': {
                            borderColor: focused ? color : hovered ? rgba(color, 0.4) : palette.hairline,
                            borderWidth: '1.5px',
                            transition: 'border-color .3s ease',
                        },
                        '&:hover fieldset': { borderColor: rgba(color, 0.5) },
                        '&.Mui-focused fieldset': { borderColor: color, borderWidth: '2px' },
                        '&.Mui-focused': {
                            boxShadow: `0 0 0 4px ${rgba(color, 0.12)}, 0 8px 32px ${rgba(color, 0.10)}`,
                            transform: 'translateY(-2px)',
                            background: rgba('#FFFFFF', 0.92),
                        },
                        '&:hover': {
                            transform: 'translateY(-1px)',
                            background: rgba('#FFFFFF', 0.72),
                        },
                    },
                    '& .MuiInputLabel-root': {
                        color: focused ? color : hovered ? palette.inkSoft : palette.inkLight,
                        fontFamily: fontBody,
                        fontWeight: 500,
                        transition: 'color .3s ease',
                        '&.Mui-focused': { color },
                    },
                    '& .MuiInputBase-input': {
                        fontFamily: fontBody,
                    },
                }}
                slotProps={{ input: {
                    startAdornment: (
                        <InputAdornment position="start">
                            <motion.div
                                animate={{
                                    scale: focused ? 1.15 : hovered ? 1.05 : 1,
                                    color: focused ? color : hovered ? palette.inkSoft : palette.inkLight,
                                }}
                                transition={{ duration: 0.3 }}
                            >
                                {icon}
                            </motion.div>
                        </InputAdornment>
                    ),
                    ...props.InputProps,
                } }}
                {...props}
            />
        </motion.div>
    );
};

/* ═══════════════════════════════════════════════════════════════════════════
   5 · UTILITY FUNCTIONS
   ═══════════════════════════════════════════════════════════════════════════ */

const playNotificationSound = () => {
    try {
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = audioCtx.createOscillator();
        const gainNode = audioCtx.createGain();
        oscillator.connect(gainNode);
        gainNode.connect(audioCtx.destination);
        oscillator.frequency.setValueAtTime(880, audioCtx.currentTime);
        oscillator.frequency.setValueAtTime(660, audioCtx.currentTime + 0.1);
        oscillator.frequency.setValueAtTime(880, audioCtx.currentTime + 0.2);
        gainNode.gain.setValueAtTime(0.3, audioCtx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.3);
        oscillator.start(audioCtx.currentTime);
        oscillator.stop(audioCtx.currentTime + 0.3);
    } catch (error) {
        console.debug('[support notification sound unavailable]', error);
    }
};

const playMessageSound = () => {
    try {
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = audioCtx.createOscillator();
        const gainNode = audioCtx.createGain();
        oscillator.connect(gainNode);
        gainNode.connect(audioCtx.destination);
        oscillator.frequency.setValueAtTime(600, audioCtx.currentTime);
        oscillator.frequency.exponentialRampToValueAtTime(800, audioCtx.currentTime + 0.15);
        gainNode.gain.setValueAtTime(0.2, audioCtx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.15);
        oscillator.start(audioCtx.currentTime);
        oscillator.stop(audioCtx.currentTime + 0.15);
    } catch (error) {
        console.debug('[support message sound unavailable]', error);
    }
};

const formatMessageTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
};

const formatFullTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    });
};

const getStatusColor = (status) => {
    switch (status) {
        case 'open': return palette.warning;
        case 'in_progress': return palette.indigo;
        case 'resolved': return palette.success;
        case 'closed': return palette.inkSoft;
        default: return palette.inkSoft;
    }
};

const getStatusLabel = (status) => {
    switch (status) {
        case 'open': return 'Open';
        case 'in_progress': return 'In Progress';
        case 'resolved': return 'Resolved';
        case 'closed': return 'Closed';
        default: return status || 'Open';
    }
};

/* ═══════════════════════════════════════════════════════════════════════════
   6 · GLASS SUPPORT TICKET CARD
   ═══════════════════════════════════════════════════════════════════════════ */

const GlassSupportTicketCard = ({ ticket, onSelect, selected }) => {
    const { ref, onPointerMove, onPointerLeave } = useSheen();

    const statusColor = getStatusColor(ticket.status);
    const priorityColor = ticket.priority === 'urgent' ? palette.danger : ticket.priority === 'high' ? palette.warning : palette.teal;
    const timeAgo = ticket.createdAt?.toDate?.() || new Date(ticket.createdAt);
    const diff = Date.now() - timeAgo.getTime();
    const minutes = Math.floor(diff / 60000);
    let timeStr = 'Just now';
    if (minutes > 60) timeStr = `${Math.floor(minutes / 60)}h ago`;
    else if (minutes > 0) timeStr = `${minutes}m ago`;

    const hasUnread = ticket.unreadCount > 0;

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            style={{ width: '100%' }}
        >
            <ListItemButton
                ref={ref}
                onPointerMove={onPointerMove}
                onPointerLeave={onPointerLeave}
                selected={selected}
                onClick={() => onSelect(ticket)}
                sx={{
                    borderRadius: '14px',
                    mb: 1,
                    p: 2,
                    position: 'relative',
                    border: `2px solid ${selected ? palette.plum : 'transparent'}`,
                    background: selected ? rgba(palette.plum, 0.08) : 'transparent',
                    transition: 'all 0.3s cubic-bezier(.22,1,.36,1)',
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        inset: 0,
                        pointerEvents: 'none',
                        opacity: 'var(--sheen, 0)',
                        transition: 'opacity .45s ease',
                        background: `radial-gradient(300px circle at var(--mx, 50%) var(--my, 0%), rgba(255,255,255,0.6), ${rgba(palette.plum, 0.08)} 40%, transparent 70%)`,
                        mixBlendMode: 'soft-light',
                        borderRadius: '14px',
                    },
                    '&:hover': {
                        background: selected ? rgba(palette.plum, 0.12) : rgba(palette.ink, 0.04),
                        transform: 'translateX(4px)',
                    },
                }}
            >
                {hasUnread && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: palette.plum,
                            animation: 'tfPulse 2s infinite',
                            boxShadow: `0 0 8px ${rgba(palette.plum, 0.4)}`,
                        }}
                    />
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <Badge
                        overlap="circular"
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        variant="dot"
                        sx={{
                            '& .MuiBadge-badge': {
                                bgcolor: statusColor,
                                boxShadow: `0 0 0 2px ${palette.paper}`,
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                            }
                        }}
                    >
                        <Avatar
                            sx={{
                                bgcolor: ticket.status === 'resolved' || ticket.status === 'closed' 
                                    ? palette.inkSoft 
                                    : palette.plum,
                                width: 44,
                                height: 44,
                                borderRadius: '12px',
                                border: `2px solid ${rgba(statusColor, 0.3)}`,
                            }}
                        >
                            {ticket.companyName?.charAt(0) || '?'}
                        </Avatar>
                    </Badge>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography
                                variant="body2"
                                fontWeight={600}
                                sx={{ 
                                    color: palette.ink,
                                    fontSize: '0.85rem',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    maxWidth: '60%',
                                }}
                            >
                                {ticket.subject || 'Support Request'}
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{ color: palette.inkLight, fontSize: '0.6rem', fontFamily: fontMono }}
                            >
                                {timeStr}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5, flexWrap: 'wrap' }}>
                            <MetaPill label={getStatusLabel(ticket.status)} tone={statusColor} />
                            <MetaPill label={ticket.priority || 'Normal'} tone={priorityColor} />
                            <MetaPill label={ticket.category || 'General'} tone={palette.inkLight} />
                        </Box>
                        {ticket.lastMessage && (
                            <Typography
                                variant="caption"
                                sx={{ 
                                    color: palette.inkSoft,
                                    fontSize: '0.65rem',
                                    display: 'block',
                                    mt: 0.5,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    maxWidth: '90%',
                                }}
                            >
                                {ticket.lastMessage}
                            </Typography>
                        )}
                        {ticket.assignedTo && (
                            <MetaPill label={ticket.assignedTo} tone={palette.plum} icon={<PersonIcon sx={{ fontSize: 12 }} />} />
                        )}
                    </Box>
                </Box>
            </ListItemButton>
        </motion.div>
    );
};

/* ═══════════════════════════════════════════════════════════════════════════
   7 · GLASS MESSAGE
   ═══════════════════════════════════════════════════════════════════════════ */

const GlassMessage = ({ message, index, isOwn, userDataProp }) => {
    const { ref, onPointerMove, onPointerLeave } = useSheen();
    const [isHovered, setIsHovered] = useState(false);

    // Use message.id or a unique key
    const messageKey = message.id || `msg-${index}`;

    return (
        <motion.div
            key={messageKey}
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.25, delay: Math.min(index * 0.015, 0.3), ease: [0.22, 1, 0.36, 1] }}
            style={{ maxWidth: '100%' }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: isOwn ? 'flex-end' : 'flex-start',
                    mb: 1.5,
                    px: 1,
                }}
            >
                {!isOwn && (
                    <Avatar
                        sx={{
                            width: 32,
                            height: 32,
                            mr: 1.5,
                            mt: 0.5,
                            bgcolor: palette.rust,
                            fontSize: '0.8rem',
                            fontWeight: 700,
                            border: `2px solid ${rgba(palette.rust, 0.3)}`,
                            fontFamily: fontMono,
                        }}
                    >
                        {message.senderName?.charAt(0) || '?'}
                    </Avatar>
                )}
                <Box sx={{ maxWidth: isOwn ? '75%' : '80%' }}>
                    {!isOwn && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, ml: 0.5 }}>
                            <Typography
                                variant="caption"
                                sx={{ fontWeight: 600, color: palette.ink, fontSize: '0.75rem', fontFamily: fontBody }}
                            >
                                {message.senderName || 'User'}
                            </Typography>
                            <MetaPill label="Customer" tone={palette.rust} />
                        </Box>
                    )}
                    {isOwn && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, mb: 0.5, mr: 0.5 }}>
                            <MetaPill label="Admin" tone={palette.plum} />
                        </Box>
                    )}
                    <Paper
                        ref={ref}
                        onPointerMove={onPointerMove}
                        onPointerLeave={onPointerLeave}
                        elevation={0}
                        sx={{
                            ...liquid(isOwn ? palette.plum : palette.rust, { radius: 18 }),
                            p: 2,
                            borderRadius: isOwn ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                            border: `1px solid ${isOwn ? rgba(palette.plum, 0.15) : rgba(palette.rust, 0.15)}`,
                            boxShadow: isOwn ? `0 4px 20px ${rgba(palette.plum, 0.1)}` : `0 4px 20px ${rgba(palette.rust, 0.06)}`,
                            '&::after': {
                                content: '""',
                                position: 'absolute',
                                inset: 0,
                                pointerEvents: 'none',
                                opacity: 'var(--sheen, 0)',
                                transition: 'opacity .45s ease',
                                background: `radial-gradient(300px circle at var(--mx, 50%) var(--my, 0%), rgba(255,255,255,0.6), ${rgba(isOwn ? palette.plum : palette.rust, 0.08)} 40%, transparent 70%)`,
                                mixBlendMode: 'soft-light',
                                borderRadius: '18px',
                            },
                            position: 'relative',
                            overflow: 'hidden',
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                color: palette.ink,
                                fontSize: '0.9rem',
                                lineHeight: 1.6,
                                fontFamily: fontBody,
                                position: 'relative',
                                zIndex: 1,
                            }}
                        >
                            {message.text}
                        </Typography>

                        {message.attachments && message.attachments.length > 0 && (
                            <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1, position: 'relative', zIndex: 1 }}>
                                {message.attachments.map((file, idx) => (
                                    <Paper
                                        key={`${messageKey}-file-${idx}`}
                                        elevation={0}
                                        sx={{
                                            p: 1,
                                            borderRadius: '10px',
                                            background: rgba(palette.ink, 0.04),
                                            border: `1px solid ${palette.hairlineSoft}`,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                                background: rgba(palette.ink, 0.08),
                                                transform: 'translateY(-2px)',
                                            }
                                        }}
                                        onClick={() => openSafeExternalUrl(file.url)}
                                    >
                                        {file.type?.startsWith('image/') ? (
                                            <Box
                                                component="img"
                                                src={file.url}
                                                alt={file.name}
                                                sx={{
                                                    width: 40,
                                                    height: 40,
                                                    borderRadius: '8px',
                                                    objectFit: 'cover',
                                                    border: `1px solid ${palette.hairlineSoft}`,
                                                }}
                                            />
                                        ) : (
                                            <FileIcon sx={{ color: palette.teal, fontSize: 24 }} />
                                        )}
                                        <Box sx={{ minWidth: 0, maxWidth: 120 }}>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    display: 'block',
                                                    fontWeight: 600,
                                                    fontSize: '0.65rem',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    color: palette.ink,
                                                    fontFamily: fontBody,
                                                }}
                                            >
                                                {file.name}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    fontSize: '0.55rem',
                                                    color: palette.inkLight,
                                                    fontFamily: fontMono,
                                                }}
                                            >
                                                {(file.size / 1024).toFixed(0)} KB
                                            </Typography>
                                        </Box>
                                    </Paper>
                                ))}
                            </Box>
                        )}

                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1, position: 'relative', zIndex: 1 }}>
                            <Typography
                                variant="caption"
                                sx={{ color: palette.inkLight, fontSize: '0.6rem', fontFamily: fontMono }}
                            >
                                {formatMessageTime(message.createdAt)}
                            </Typography>
                        </Box>
                    </Paper>
                </Box>
                {isOwn && (
                    <Avatar
                        sx={{
                            width: 32,
                            height: 32,
                            ml: 1.5,
                            mt: 0.5,
                            bgcolor: palette.plum,
                            fontSize: '0.8rem',
                            fontWeight: 700,
                            border: `2px solid ${rgba(palette.plum, 0.3)}`,
                            fontFamily: fontMono,
                        }}
                    >
                        {userDataProp?.displayName?.charAt(0) || 'A'}
                    </Avatar>
                )}
            </Box>
        </motion.div>
    );
};

const withTimeout = (promise, ms, message) => new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error(message)), ms);
    Promise.resolve(promise).then(
        (value) => { window.clearTimeout(timer); resolve(value); },
        (error) => { window.clearTimeout(timer); reject(error); }
    );
});

/* ═══════════════════════════════════════════════════════════════════════════
   8 · MAIN ADMIN SUPPORT COMPONENT
   ═══════════════════════════════════════════════════════════════════════════ */

const AdminSupport = () => {
    const navigate = useNavigate();
    const { user, userData } = useAuth();

    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [tickets, setTickets] = useState([]);
    const [activeTicket, setActiveTicket] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [typingUsers, setTypingUsers] = useState({});
    const [search, setSearch] = useState('');
    const [filters, setFilters] = useState({ status: '', priority: '', category: '' });
    const [showStatusDialog, setShowStatusDialog] = useState(false);
    const [showAssignDialog, setShowAssignDialog] = useState(false);
    const [showTicketDetails, setShowTicketDetails] = useState(false);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [showQuickReplies, setShowQuickReplies] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState('');
    const [selectedPriority, setSelectedPriority] = useState('');
    const [assignedAgent, setAssignedAgent] = useState('');
    const [availableAgents, setAvailableAgents] = useState([]);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadStatus, setUploadStatus] = useState('idle');
    const [soundEnabled, setSoundEnabled] = useState(true);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

    const [ticketStats, setTicketStats] = useState({
        total: 0,
        open: 0,
        inProgress: 0,
        resolved: 0,
        closed: 0,
        urgent: 0,
        avgResponseTime: 0,
    });

    const messagesEndRef = useRef(null);
    const fileInputRef = useRef(null);
    const typingTimeoutRef = useRef(null);

    const isAdmin = ['admin', 'super_admin', 'superadmin', 'platform_admin'].includes(String(userData?.role || userData?.userRole || '').toLowerCase().replace(/[\s-]+/g, '_'));

    // ── fetch tickets ─────────────────────────────────────────────────────

    const stampMs = useCallback((value) => {
        if (!value) return 0;
        if (typeof value?.toMillis === 'function') return value.toMillis();
        if (typeof value?.toDate === 'function') return value.toDate().getTime();
        const parsed = new Date(value).getTime();
        return Number.isFinite(parsed) ? parsed : 0;
    }, []);

    const fetchTickets = useCallback(async (silent = false) => {
        if (silent) setRefreshing(true);
        else setLoading(true);
        setLoadError('');

        try {
            // Keep the Support page usable even when Firestore is offline or a
            // rules/index problem prevents the initial query from resolving.
            const snapshot = await withTimeout(
                getDocs(collection(db, 'supportTickets')),
                8000,
                'Support tickets took too long to load. Check Firestore access and try again.'
            );
            const list = snapshot.docs.map((ticketDoc) => ({ id: ticketDoc.id, ...(ticketDoc.data() || {}) }))
                .sort((a, b) => stampMs(b.createdAt || b.updatedAt || b.lastMessageAt) - stampMs(a.createdAt || a.updatedAt || a.lastMessageAt));
            const stats = { total: 0, open: 0, inProgress: 0, resolved: 0, closed: 0, urgent: 0, avgResponseTime: 0 };
            let respondedTickets = 0;

            list.forEach((data) => {
                stats.total += 1;
                switch (data.status) {
                    case 'open': stats.open += 1; break;
                    case 'in_progress': stats.inProgress += 1; break;
                    case 'resolved': stats.resolved += 1; break;
                    case 'closed': stats.closed += 1; break;
                    default: stats.open += 1;
                }
                if (data.priority === 'urgent') stats.urgent += 1;
                const created = stampMs(data.createdAt);
                const responded = stampMs(data.firstResponseAt);
                if (created && responded && responded >= created) {
                    stats.avgResponseTime += (responded - created) / (1000 * 60);
                    respondedTickets += 1;
                }
            });
            if (respondedTickets > 0) stats.avgResponseTime = Math.round(stats.avgResponseTime / respondedTickets);

            setTicketStats(stats);
            setTickets(list);
            setActiveTicket((current) => {
                if (current && list.some((item) => item.id === current.id)) {
                    return list.find((item) => item.id === current.id) || current;
                }
                const urgent = list.find((item) => item.priority === 'urgent' && !['resolved', 'closed'].includes(item.status));
                const open = list.find((item) => ['open', 'in_progress'].includes(item.status));
                return urgent || open || list[0] || null;
            });

            // Agent lookup is optional UI metadata. Never keep the whole Support
            // page loading while this secondary collection is unavailable.
            withTimeout(getDocs(collection(db, 'supportAgents')), 4000, 'Support agents lookup timed out')
                .then((agentsSnap) => setAvailableAgents(agentsSnap.docs.map((agentDoc) => ({ id: agentDoc.id, ...(agentDoc.data() || {}) }))))
                .catch((agentError) => {
                    console.warn('Support agent directory unavailable:', agentError?.message || agentError);
                    setAvailableAgents([]);
                });
        } catch (error) {
            console.error('Error fetching tickets:', error);
            const message = friendlyError(error, 'Failed to load support tickets');
            setLoadError(message);
            toast.error(message);
            setTickets([]);
            setActiveTicket(null);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [stampMs]);

    const fetchMessages = useCallback((ticketId) => {
        if (!ticketId) { setMessages([]); return undefined; }
        const messagesRef = collection(db, 'supportTickets', ticketId, 'messages');
        const unsubscribe = onSnapshot(messagesRef, (snapshot) => {
            const list = snapshot.docs.map((messageDoc) => ({ id: messageDoc.id, ...(messageDoc.data() || {}) }))
                .sort((a, b) => stampMs(a.createdAt || a.timestamp) - stampMs(b.createdAt || b.timestamp));
            const unread = list.filter((message) => !message.read && message.senderType !== 'admin');
            setMessages(list);
            if (unread.length > 0 && soundEnabled) playNotificationSound();
            if (unread.length > 0) {
                void (async () => {
                    try {
                        for (let offset = 0; offset < unread.length; offset += 450) {
                            const batch = writeBatch(db);
                            unread.slice(offset, offset + 450).forEach((message) => batch.update(doc(db, 'supportTickets', ticketId, 'messages', message.id), { read: true, readAt: serverTimestamp() }));
                            await batch.commit();
                        }
                    } catch (readError) {
                        console.warn('Could not mark support messages read:', readError?.message || readError);
                    }
                })();
            }
        }, (error) => {
            console.error('Support message listener failed:', error);
            toast.error('Could not load messages for this ticket.');
        });

        const ticketUnsub = onSnapshot(doc(db, 'supportTickets', ticketId), (ticketDoc) => {
            if (!ticketDoc.exists()) return;
            const updated = { id: ticketDoc.id, ...(ticketDoc.data() || {}) };
            setActiveTicket(updated);
            setTickets((prev) => prev.map((ticket) => ticket.id === updated.id ? updated : ticket));
        }, (error) => console.warn('Support ticket listener unavailable:', error?.message || error));

        return () => { unsubscribe(); ticketUnsub(); };
    }, [soundEnabled, stampMs]);

    useEffect(() => {
        if (!isAdmin || !user?.uid) {
            setLoading(false);
            return undefined;
        }
        fetchTickets();
        return undefined;
    }, [fetchTickets, isAdmin, user?.uid]);

    useEffect(() => {
        const cleanup = fetchMessages(activeTicket?.id);
        return typeof cleanup === 'function' ? cleanup : undefined;
    }, [activeTicket?.id, fetchMessages]);

    // ── send message ─────────────────────────────────────────────────────

    const sendMessage = async (text, attachments = []) => {
        if (!text.trim() && attachments.length === 0) return;
        if (!activeTicket) { toast.error('No active ticket selected'); return; }

        setIsSending(true);
        try {
            const messageData = {
                text: text.trim(),
                senderId: user?.uid || 'admin',
                senderName: userData?.displayName || 'Support Agent',
                senderType: 'admin',
                timestamp: serverTimestamp(),
                createdAt: serverTimestamp(),
                read: false,
                status: 'sent',
                attachments: attachments,
            };

            await addDoc(collection(db, 'supportTickets', activeTicket.id, 'messages'), messageData);

            if (soundEnabled) playMessageSound();

            const ticketRef = doc(db, 'supportTickets', activeTicket.id);
            await updateDoc(ticketRef, {
                lastMessage: text.trim().slice(0, 100),
                lastMessageAt: serverTimestamp(),
                lastMessageBy: userData?.displayName || 'Support Agent',
                updatedAt: serverTimestamp(),
                ...(activeTicket.status === 'open' && !activeTicket.firstResponseAt ? { firstResponseAt: serverTimestamp() } : {}),
            });

            if (activeTicket.status === 'open') {
                await updateDoc(ticketRef, { status: 'in_progress' });
                setActiveTicket(prev => ({ ...prev, status: 'in_progress' }));
            }

            setNewMessage('');
            setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);

            const typingRef = doc(db, 'supportTickets', activeTicket.id, 'typing', 'status');
            await setDoc(typingRef, {
                userId: user?.uid || 'admin',
                userName: userData?.displayName || 'Support Agent',
                isTyping: false,
                updatedAt: serverTimestamp(),
            });

        } catch (error) {
            console.error('Error sending message:', error);
            toast.error('Failed to send message');
        } finally {
            setIsSending(false);
            setUploadStatus('idle');
        }
    };

    // ── handlers ─────────────────────────────────────────────────────────

    const handleTyping = async (isTypingNow) => {
        if (!activeTicket) return;
        try {
            const typingRef = doc(db, 'supportTickets', activeTicket.id, 'typing', 'status');
            await setDoc(typingRef, {
                userId: user?.uid || 'admin',
                userName: userData?.displayName || 'Support Agent',
                isTyping: isTypingNow,
                updatedAt: serverTimestamp(),
            });
            if (isTypingNow) {
                clearTimeout(typingTimeoutRef.current);
                typingTimeoutRef.current = setTimeout(() => handleTyping(false), 3000);
            }
        } catch (error) {
            console.error('Error updating typing status:', error);
        }
    };

    const handleFileUpload = async (event) => {
        const files = Array.from(event.target.files);
        if (files.length === 0) return;

        setUploadStatus('uploading');
        setUploadProgress(0);

        try {
            const uploaded = [];
            let completed = 0;
            const total = files.length;

            for (const file of files) {
                const asset = await uploadAdminFile({
                    file, scope: 'platform', folder: `support/${activeTicket?.id || 'draft'}`,
                    onProgress: (progress) => setUploadProgress(Math.round(((completed + progress / 100) / total) * 100)),
                });
                const result = { name: file.name, type: file.type, size: file.size, url: asset.url, path: asset.path, asset };

                uploaded.push(result);
                completed++;
                setUploadProgress(Math.round((completed / total) * 100));
            }

            setUploadStatus('complete');
            await sendMessage('', uploaded);
            toast.success(`${uploaded.length} file(s) uploaded and sent`);

        } catch (error) {
            console.error('Error uploading files:', error);
            setUploadStatus('error');
            toast.error(friendlyError(error, 'Failed to upload files'));
        } finally {
            setUploadStatus('idle');
            setUploadProgress(0);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const updateTicketStatus = async (ticketId, status) => {
        try {
            const ticketRef = doc(db, 'supportTickets', ticketId);
            const updates = { status, updatedAt: serverTimestamp() };
            if (status === 'resolved') updates.resolvedAt = serverTimestamp();
            if (status === 'closed') updates.closedAt = serverTimestamp();
            await updateDoc(ticketRef, updates);
            toast.success(`Ticket status updated to ${status.replace('_', ' ')}`);
            if (soundEnabled) playNotificationSound();
            setShowStatusDialog(false);
            fetchTickets(true);
        } catch (error) {
            console.error('Error updating status:', error);
            toast.error('Failed to update status');
        }
    };

    const assignTicket = async (ticketId, agentName) => {
        try {
            const ticketRef = doc(db, 'supportTickets', ticketId);
            await updateDoc(ticketRef, { assignedTo: agentName, updatedAt: serverTimestamp() });
            toast.success(`Ticket assigned to ${agentName}`);
            setShowAssignDialog(false);
            fetchTickets(true);
        } catch (error) {
            console.error('Error assigning ticket:', error);
            toast.error('Failed to assign ticket');
        }
    };

    const filtersActive = Boolean(search || filters.status || filters.priority || filters.category);

    const filteredTickets = tickets.filter(ticket => {
        const matchesSearch = ticket.subject?.toLowerCase().includes(search.toLowerCase()) ||
            ticket.companyName?.toLowerCase().includes(search.toLowerCase()) ||
            ticket.lastMessage?.toLowerCase().includes(search.toLowerCase());
        const matchesStatus = !filters.status || ticket.status === filters.status;
        const matchesPriority = !filters.priority || ticket.priority === filters.priority;
        const matchesCategory = !filters.category || ticket.category === filters.category;
        return matchesSearch && matchesStatus && matchesPriority && matchesCategory;
    });

    // ── render ─────────────────────────────────────────────────────────────

    const shell = {
        minHeight: '100vh',
        position: 'relative',
        fontFamily: fontBody,
        color: palette.ink,
        background: `linear-gradient(180deg, ${palette.canvas} 0%, ${palette.canvasDeep} 100%)`,
        '&::before': {
            content: '""',
            position: 'fixed',
            inset: 0,
            zIndex: 0,
            pointerEvents: 'none',
            opacity: 0.3,
            backgroundImage:
                'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%27140%27 height=%27140%27%3E%3Cfilter id=%27n%27%3E%3CfeTurbulence type=%27fractalNoise%27 baseFrequency=%270.85%27 numOctaves=%272%27 stitchTiles=%27stitch%27/%3E%3C/filter%3E%3Crect width=%27100%25%27 height=%27100%25%27 filter=%27url(%23n)%27 opacity=%270.03%27/%3E%3C/svg%3E")',
            animation: 'tfGrain 8s steps(6) infinite',
        },
    };

    const statCards = [
        { label: 'Total Tickets', value: ticketStats.total, icon: <AssignmentIcon  key="config-1522-0-2" />, accent: palette.inkSoft, hint: 'All tickets' },
        { label: 'Open', value: ticketStats.open, icon: <WarningIcon  key="config-1523-1-2" />, accent: palette.warning, hint: 'Needs attention' },
        { label: 'In Progress', value: ticketStats.inProgress, icon: <TimerIcon  key="config-1524-2-2" />, accent: palette.indigo, hint: 'Working on it' },
        { label: 'Urgent', value: ticketStats.urgent, icon: <FlagIcon  key="config-1525-3-2" />, accent: palette.danger, hint: 'Priority' },
    ];

    if (!isAdmin) {
        return (
            <Box sx={shell}>
                <style>{FONT_IMPORT}</style>
                <LiquidBackdrop />
                
                <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1, py: 10 }}>
                    <GlassPanel accent={palette.danger}>
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <GlassIcon icon={<SupportAgentIcon />} color={palette.danger} size={56} />
                            <Typography
                                sx={{ fontFamily: fontDisplay, fontWeight: 600, fontSize: '1.2rem', color: palette.ink, mt: 2 }}
                            >
                                Access Denied
                            </Typography>
                            <Typography sx={{ color: palette.inkLight, fontSize: '0.9rem' }}>
                                You do not have admin privileges to access this page.
                            </Typography>
                            <Button
                                onClick={() => navigate('/dashboard')}
                                sx={{
                                    mt: 3,
                                    px: 3,
                                    py: 1,
                                    borderRadius: '14px',
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    color: '#fff',
                                    background: `linear-gradient(140deg, ${palette.rust}, ${palette.rustDeep})`,
                                    boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.3)}, 0 14px 30px -14px ${rgba(palette.rust, 1)}`,
                                    '&:hover': { background: `linear-gradient(140deg, ${palette.rustGlow}, ${palette.rust})` },
                                }}
                            >
                                Return to Dashboard
                            </Button>
                        </Box>
                    </GlassPanel>
                </Container>
            </Box>
        );
    }


    return (
        <Box sx={shell}>
            <style>{FONT_IMPORT}</style>
            <LiquidBackdrop />
            

            <Container
                maxWidth={false}
                disableGutters
                sx={{ position: 'relative', zIndex: 1, px: { xs: 2, sm: 3, lg: 5 }, py: { xs: 3, md: 4 } }}
            >
                {loadError ? (
                    <Alert severity="warning" sx={{ mb: 2.2, borderRadius: 2.5 }} action={
                        <Button color="inherit" size="small" onClick={() => fetchTickets(false)}>Retry</Button>
                    }>
                        {loadError}
                    </Alert>
                ) : null}
                {/* Hero */}
                <motion.div
                    initial={{ opacity: 0, y: -14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-end',
                            justifyContent: 'space-between',
                            gap: 2,
                            flexWrap: 'wrap',
                            mb: 3.5,
                        }}
                    >
                        <Box sx={{ minWidth: 0 }}>
                            <Typography
                                sx={{
                                    fontFamily: fontMono,
                                    fontSize: '0.64rem',
                                    fontWeight: 500,
                                    letterSpacing: '0.22em',
                                    textTransform: 'uppercase',
                                    color: palette.plum,
                                    mb: 0.75,
                                }}
                            >
                                Admin Support
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: fontDisplay,
                                    fontWeight: 600,
                                    fontSize: { xs: '1.9rem', md: '2.6rem' },
                                    letterSpacing: '-0.035em',
                                    lineHeight: 1.05,
                                    color: palette.ink,
                                }}
                            >
                                Support{' '}
                                <Box
                                    component="span"
                                    sx={{
                                        fontStyle: 'italic',
                                        background: `linear-gradient(120deg, ${palette.plum}, ${palette.indigo})`,
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                    }}
                                >
                                    center
                                </Box>
                            </Typography>
                            <Typography
                                sx={{ color: palette.inkSoft, fontSize: '0.92rem', mt: 0.75, maxWidth: 560 }}
                            >
                                Manage customer support tickets and provide assistance in real-time.
                            </Typography>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.75, flexWrap: 'wrap' }}>
                                <Box
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 0.75,
                                        px: 1.25,
                                        height: 30,
                                        borderRadius: '999px',
                                        border: `1px solid ${rgba(palette.pine, 0.22)}`,
                                        background: `linear-gradient(140deg, ${rgba('#FFFFFF', 0.75)}, ${rgba(palette.pine, 0.1)})`,
                                        backdropFilter: 'blur(10px)',
                                    }}
                                >
                                    <LiveIcon
                                        sx={{
                                            fontSize: 9,
                                            color: palette.pine,
                                            animation: 'tfPulse 2s ease-in-out infinite',
                                        }}
                                    />
                                    <Typography
                                        sx={{
                                            fontFamily: fontMono,
                                            fontSize: '0.66rem',
                                            fontWeight: 600,
                                            color: palette.pine,
                                        }}
                                    >
                                        {compact(ticketStats.open)} OPEN
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 0.75,
                                        px: 1.25,
                                        height: 30,
                                        borderRadius: '999px',
                                        border: `1px solid ${rgba(palette.danger, 0.22)}`,
                                        background: `linear-gradient(140deg, ${rgba('#FFFFFF', 0.75)}, ${rgba(palette.danger, 0.1)})`,
                                        backdropFilter: 'blur(10px)',
                                    }}
                                >
                                    <FlagIcon sx={{ fontSize: 13, color: palette.danger }} />
                                    <Typography
                                        sx={{
                                            fontFamily: fontMono,
                                            fontSize: '0.66rem',
                                            fontWeight: 600,
                                            color: palette.danger,
                                        }}
                                    >
                                        {compact(ticketStats.urgent)} URGENT
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 0.75,
                                        px: 1.25,
                                        height: 30,
                                        borderRadius: '999px',
                                        border: `1px solid ${rgba(palette.indigo, 0.22)}`,
                                        background: `linear-gradient(140deg, ${rgba('#FFFFFF', 0.75)}, ${rgba(palette.indigo, 0.1)})`,
                                        backdropFilter: 'blur(10px)',
                                    }}
                                >
                                    <TimerIcon sx={{ fontSize: 13, color: palette.indigo }} />
                                    <Typography
                                        sx={{
                                            fontFamily: fontMono,
                                            fontSize: '0.66rem',
                                            fontWeight: 600,
                                            color: palette.indigo,
                                        }}
                                    >
                                        {compact(ticketStats.inProgress)} IN PROGRESS
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
                            <IconButton
                                onClick={() => setSoundEnabled(!soundEnabled)}
                                sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: '12px',
                                    border: `1px solid ${palette.hairline}`,
                                    background: rgba('#FFFFFF', 0.6),
                                    backdropFilter: 'blur(10px)',
                                    color: soundEnabled ? palette.plum : palette.inkSoft,
                                    transition: 'all .3s ease',
                                    '&:hover': { background: rgba('#FFFFFF', 0.9), transform: 'translateY(-2px)' },
                                }}
                            >
                                {soundEnabled ? <VolumeUpIcon /> : <VolumeOffIcon />}
                            </IconButton>
                            <Button
                                onClick={() => fetchTickets(true)}
                                disabled={refreshing}
                                startIcon={<RefreshIcon sx={{ animation: refreshing ? 'tfSpin 1s linear infinite' : 'none' }} />}
                                sx={{
                                    px: 2.25,
                                    py: 1,
                                    borderRadius: '14px',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: '0.82rem',
                                    color: palette.inkSoft,
                                    border: `1px solid ${rgba('#FFFFFF', 0.7)}`,
                                    background: `linear-gradient(140deg, ${rgba('#FFFFFF', 0.8)}, ${rgba('#FFFFFF', 0.45)})`,
                                    backdropFilter: 'blur(14px)',
                                    boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.9)}, 0 8px 20px -12px ${rgba(palette.ink, 0.5)}`,
                                    '&:hover': { color: palette.ink, background: rgba('#FFFFFF', 0.9) },
                                }}
                            >
                                {refreshing ? 'Refreshing' : 'Refresh'}
                            </Button>
                        </Box>
                    </Box>
                </motion.div>

                {/* Stats */}
                <Box
                    sx={{
                        display: 'grid',
                        gap: 2,
                        mb: 3,
                        gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' },
                    }}
                >
                    {statCards.map((s, i) => (
                        <StatCard key={s.label} {...s} delay={i * 0.06} loading={loading} />
                    ))}
                </Box>

                {/* Main Support Area */}
                <GlassPanel accent={palette.plum} delay={0.1} padding={{ xs: 1.75, md: 2.5 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            gap: 2.5,
                            minHeight: '60vh',
                        }}
                    >
                        {/* Ticket List */}
                        <Box sx={{ width: { xs: '100%', md: '340px' }, flexShrink: 0 }}>
                            <PanelHead
                                icon={<AssignmentIcon />}
                                title="Tickets"
                                subtitle={`${compact(filteredTickets.length)} active`}
                                accent={palette.plum}
                                dense
                            />
                            
                            {/* Search & Filters */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
                                <GlassInput
                                    label="Search tickets"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    icon={<SearchIcon sx={{ color: palette.inkLight }} />}
                                    color={palette.plum}
                                    size="small"
                                />
                                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                                    <Chip
                                        label="All"
                                        size="small"
                                        onClick={() => { setFilters({ status: '', priority: '', category: '' }); }}
                                        sx={{
                                            bgcolor: !filters.status && !filters.priority && !filters.category ? palette.plum : 'transparent',
                                            color: !filters.status && !filters.priority && !filters.category ? '#fff' : palette.inkLight,
                                            fontWeight: 600,
                                            borderRadius: '8px',
                                            fontSize: '0.65rem',
                                            border: `1px solid ${!filters.status && !filters.priority && !filters.category ? palette.plum : palette.hairlineSoft}`,
                                            transition: 'all .2s ease',
                                            '&:hover': { transform: 'translateY(-2px)' },
                                        }}
                                    />
                                    {['open', 'in_progress', 'resolved', 'closed'].map((status) => (
                                        <Chip
                                            key={status}
                                            label={status.replace('_', ' ')}
                                            size="small"
                                            onClick={() => setFilters(prev => ({ ...prev, status: prev.status === status ? '' : status }))}
                                            sx={{
                                                bgcolor: filters.status === status ? palette.warning : 'transparent',
                                                color: filters.status === status ? '#fff' : palette.inkLight,
                                                fontWeight: 600,
                                                borderRadius: '8px',
                                                fontSize: '0.65rem',
                                                border: `1px solid ${filters.status === status ? palette.warning : palette.hairlineSoft}`,
                                                transition: 'all .2s ease',
                                                '&:hover': { transform: 'translateY(-2px)' },
                                            }}
                                        />
                                    ))}
                                    <Chip
                                        label="Urgent"
                                        size="small"
                                        onClick={() => setFilters(prev => ({ ...prev, priority: prev.priority === 'urgent' ? '' : 'urgent' }))}
                                        sx={{
                                            bgcolor: filters.priority === 'urgent' ? palette.danger : 'transparent',
                                            color: filters.priority === 'urgent' ? '#fff' : palette.inkLight,
                                            fontWeight: 600,
                                            borderRadius: '8px',
                                            fontSize: '0.65rem',
                                            border: `1px solid ${filters.priority === 'urgent' ? palette.danger : palette.hairlineSoft}`,
                                            transition: 'all .2s ease',
                                            '&:hover': { transform: 'translateY(-2px)' },
                                        }}
                                    />
                                    <AnimatePresence mode="wait">
                                        {filtersActive && (
                                            <motion.div
                                                key="clear-filters"
                                                initial={{ opacity: 0, scale: 0.9 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                                exit={{ opacity: 0, scale: 0.9 }}
                                            >
                                                <Chip
                                                    label="Clear"
                                                    size="small"
                                                    onClick={() => { setSearch(''); setFilters({ status: '', priority: '', category: '' }); }}
                                                    sx={{
                                                        bgcolor: 'transparent',
                                                        color: palette.rust,
                                                        fontWeight: 600,
                                                        borderRadius: '8px',
                                                        fontSize: '0.65rem',
                                                        border: `1px solid ${rgba(palette.rust, 0.3)}`,
                                                        cursor: 'pointer',
                                                        '&:hover': { bgcolor: rgba(palette.rust, 0.06) },
                                                    }}
                                                />
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </Box>
                            </Box>

                            {/* Ticket List */}
                            <Box sx={{ maxHeight: 'calc(60vh - 200px)', overflowY: 'auto' }}>
                                {filteredTickets.length === 0 ? (
                                    <Box sx={{ textAlign: 'center', py: 4 }}>
                                        <GlassIcon icon={<QuestionAnswerIcon />} color={palette.inkFaint} size={48} />
                                        <Typography sx={{ color: palette.inkLight, mt: 1, fontSize: '0.85rem' }}>
                                            {search || filtersActive ? 'No matching tickets' : 'No support tickets'}
                                        </Typography>
                                    </Box>
                                ) : (
                                    filteredTickets.map((ticket) => (
                                        <GlassSupportTicketCard
                                            key={ticket.id}
                                            ticket={ticket}
                                            selected={activeTicket?.id === ticket.id}
                                            onSelect={(t) => {
                                                setActiveTicket(t);
                                                fetchMessages(t.id);
                                            }}
                                        />
                                    ))
                                )}
                            </Box>
                        </Box>

                        {/* Chat Area */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            {!activeTicket ? (
                                <Box sx={{ textAlign: 'center', py: 8 }}>
                                    <GlassIcon icon={<SupportAgentIcon />} color={palette.inkFaint} size={56} />
                                    <Typography
                                        sx={{
                                            fontFamily: fontDisplay,
                                            fontWeight: 600,
                                            fontSize: '1.1rem',
                                            color: palette.ink,
                                            mt: 2,
                                        }}
                                    >
                                        Select a support ticket
                                    </Typography>
                                    <Typography sx={{ color: palette.inkLight, fontSize: '0.85rem' }}>
                                        Choose from the list to start helping customers
                                    </Typography>
                                </Box>
                            ) : (
                                <>
                                    {/* Chat Header */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            flexWrap: 'wrap',
                                            gap: 1.5,
                                            mb: 2,
                                            pb: 2,
                                            borderBottom: `1px solid ${palette.hairlineSoft}`,
                                        }}
                                    >
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography
                                                sx={{
                                                    fontFamily: fontDisplay,
                                                    fontWeight: 600,
                                                    fontSize: '1.05rem',
                                                    color: palette.ink,
                                                }}
                                            >
                                                {activeTicket.subject || 'Support Chat'}
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                                <MetaPill
                                                    label={getStatusLabel(activeTicket.status)}
                                                    tone={getStatusColor(activeTicket.status)}
                                                />
                                                <MetaPill
                                                    label={activeTicket.priority || 'Normal'}
                                                    tone={activeTicket.priority === 'urgent' ? palette.danger : activeTicket.priority === 'high' ? palette.warning : palette.teal}
                                                />
                                                {activeTicket.companyName && (
                                                    <MetaPill
                                                        label={activeTicket.companyName}
                                                        tone={palette.rust}
                                                        icon={<BusinessIcon sx={{ fontSize: 12 }} />}
                                                    />
                                                )}
                                                {activeTicket.assignedTo && (
                                                    <MetaPill
                                                        label={`Assigned: ${activeTicket.assignedTo}`}
                                                        tone={palette.plum}
                                                        icon={<PersonIcon sx={{ fontSize: 12 }} />}
                                                    />
                                                )}
                                            </Box>
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                            <Tooltip title="Details">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setShowTicketDetails(true)}
                                                    sx={{
                                                        color: palette.inkSoft,
                                                        border: `1px solid ${palette.hairlineSoft}`,
                                                        borderRadius: '10px',
                                                        p: 0.75,
                                                    }}
                                                >
                                                    <InfoIcon sx={{ fontSize: 18 }} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Assign">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setShowAssignDialog(true)}
                                                    sx={{
                                                        color: palette.inkSoft,
                                                        border: `1px solid ${palette.hairlineSoft}`,
                                                        borderRadius: '10px',
                                                        p: 0.75,
                                                    }}
                                                >
                                                    <PersonIcon sx={{ fontSize: 18 }} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Status">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => { setSelectedStatus(activeTicket.status || 'open'); setShowStatusDialog(true); }}
                                                    sx={{
                                                        color: palette.inkSoft,
                                                        border: `1px solid ${palette.hairlineSoft}`,
                                                        borderRadius: '10px',
                                                        p: 0.75,
                                                    }}
                                                >
                                                    <CheckIcon sx={{ fontSize: 18 }} />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Box>

                                    {/* Messages */}
                                    <Box sx={{ maxHeight: 'calc(60vh - 220px)', overflowY: 'auto', mb: 2 }}>
                                        {messages.length === 0 ? (
                                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                                <MessageIcon sx={{ fontSize: 40, color: palette.inkFaint, opacity: 0.3, mb: 1 }} />
                                                <Typography sx={{ color: palette.inkLight, fontSize: '0.85rem' }}>
                                                    No messages yet
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <>
                                                {messages.map((msg, i) => (
                                                    <GlassMessage
                                                        key={msg.id || `msg-${i}`}
                                                        message={msg}
                                                        index={i}
                                                        isOwn={msg.senderType === 'admin'}
                                                        userDataProp={userData}
                                                    />
                                                ))}
                                                {Object.keys(typingUsers).length > 0 && (
                                                    <Box
                                                        key="typing-indicator"
                                                        sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: 2, mb: 1 }}
                                                    >
                                                        <Avatar
                                                            sx={{
                                                                width: 28,
                                                                height: 28,
                                                                bgcolor: palette.rust,
                                                                fontSize: '0.7rem',
                                                                fontWeight: 700,
                                                            }}
                                                        >
                                                            {Object.values(typingUsers)[0]?.name?.charAt(0) || 'U'}
                                                        </Avatar>
                                                        <Box>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{ fontWeight: 600, color: palette.ink }}
                                                                >
                                                                    {Object.values(typingUsers)[0]?.name || 'User'} is typing
                                                                </Typography>
                                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                                    {[0, 0.2, 0.4].map((delay) => (
                                                                        <motion.div
                                                                            key={`dot-${delay}`}
                                                                            animate={{ y: [0, -6, 0] }}
                                                                            transition={{
                                                                                duration: 0.6,
                                                                                repeat: Infinity,
                                                                                delay,
                                                                            }}
                                                                        >
                                                                            <Box
                                                                                sx={{
                                                                                    width: 4,
                                                                                    height: 4,
                                                                                    borderRadius: '50%',
                                                                                    bgcolor: palette.inkSoft,
                                                                                }}
                                                                            />
                                                                        </motion.div>
                                                                    ))}
                                                                </Box>
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                )}
                                                <div ref={messagesEndRef} />
                                            </>
                                        )}
                                    </Box>

                                    {/* Message Input */}
                                    <Box
                                        sx={{
                                            borderTop: `1px solid ${palette.hairlineSoft}`,
                                            pt: 2,
                                        }}
                                    >
                                        {uploadStatus === 'uploading' && (
                                            <Box key="upload-progress" sx={{ mb: 1.5 }}>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        mb: 0.5,
                                                    }}
                                                >
                                                    <Typography
                                                        variant="caption"
                                                        sx={{ fontWeight: 600, color: palette.inkSoft }}
                                                    >
                                                        Uploading files...
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{ fontWeight: 600, color: palette.plum }}
                                                    >
                                                        {uploadProgress}%
                                                    </Typography>
                                                </Box>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={uploadProgress}
                                                    sx={{
                                                        height: 4,
                                                        borderRadius: 2,
                                                        bgcolor: palette.hairline,
                                                        '& .MuiLinearProgress-bar': {
                                                            background: `linear-gradient(90deg, ${palette.plum}, ${palette.indigo})`,
                                                            borderRadius: 2,
                                                        }
                                                    }}
                                                />
                                            </Box>
                                        )}

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                gap: 1.5,
                                                alignItems: 'flex-end',
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                                                <Tooltip title="Attach file">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => fileInputRef.current?.click()}
                                                        disabled={uploadStatus === 'uploading'}
                                                        sx={{
                                                            border: `1px solid ${palette.hairlineSoft}`,
                                                            borderRadius: '10px',
                                                            p: 0.75,
                                                            color: palette.inkSoft,
                                                            transition: 'all .2s ease',
                                                            '&:hover': { borderColor: palette.plum, color: palette.plum },
                                                        }}
                                                    >
                                                        <AttachFileIcon sx={{ fontSize: 18 }} />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Emoji">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                                                        sx={{
                                                            border: `1px solid ${palette.hairlineSoft}`,
                                                            borderRadius: '10px',
                                                            p: 0.75,
                                                            color: palette.inkSoft,
                                                            transition: 'all .2s ease',
                                                            '&:hover': { borderColor: palette.amber, color: palette.amber },
                                                        }}
                                                    >
                                                        <EmojiIcon sx={{ fontSize: 18 }} />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Quick replies">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setShowQuickReplies(!showQuickReplies)}
                                                        sx={{
                                                            border: `1px solid ${palette.hairlineSoft}`,
                                                            borderRadius: '10px',
                                                            p: 0.75,
                                                            color: palette.inkSoft,
                                                            transition: 'all .2s ease',
                                                            '&:hover': { borderColor: palette.pine, color: palette.pine },
                                                        }}
                                                    >
                                                        <QuickReplyIcon sx={{ fontSize: 18 }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>

                                            <GlassInput
                                                label="Type your reply..."
                                                value={newMessage}
                                                onChange={(e) => {
                                                    setNewMessage(e.target.value);
                                                    handleTyping(e.target.value.length > 0);
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' && !e.shiftKey && !isSending) {
                                                        e.preventDefault();
                                                        sendMessage(newMessage);
                                                    }
                                                }}
                                                icon={<ChatIcon sx={{ color: palette.inkLight }} />}
                                                color={palette.plum}
                                                multiline
                                                rows={2}
                                                slotProps={{ input: { sx: { fontSize: '0.875rem', py: 0.5 } } }}
                                            />

                                            <Button
                                                variant="contained"
                                                disableElevation
                                                onClick={() => sendMessage(newMessage)}
                                                disabled={(!newMessage.trim()) || isSending || uploadStatus === 'uploading'}
                                                sx={{
                                                    borderRadius: '14px',
                                                    minWidth: 50,
                                                    height: 48,
                                                    px: 2,
                                                    background: `linear-gradient(140deg, ${palette.plum}, ${palette.plumGlow})`,
                                                    boxShadow: `0 8px 20px -8px ${rgba(palette.plum, 0.6)}`,
                                                    '&:hover': {
                                                        boxShadow: `0 12px 28px -8px ${rgba(palette.plum, 0.7)}`,
                                                        transform: 'translateY(-2px)',
                                                    },
                                                    '&:disabled': {
                                                        bgcolor: palette.hairline,
                                                        color: palette.inkFaint,
                                                    },
                                                }}
                                            >
                                                {isSending ? (
                                                    <CircularProgress size={20} sx={{ color: '#fff' }} />
                                                ) : (
                                                    <SendIcon />
                                                )}
                                            </Button>
                                        </Box>

                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            multiple
                                            style={{ display: 'none' }}
                                            onChange={handleFileUpload}
                                            accept="image/*,.pdf,.doc,.docx,.txt,.xlsx,.xls,.ppt,.pptx,.zip,.rar"
                                        />

                                        <AnimatePresence mode="wait">
                                            {showEmojiPicker && (
                                                <motion.div
                                                    key="emoji-picker"
                                                    initial={{ opacity: 0, height: 0 }}
                                                    animate={{ opacity: 1, height: 'auto' }}
                                                    exit={{ opacity: 0, height: 0 }}
                                                >
                                                    <Box
                                                        sx={{
                                                            mt: 1.5,
                                                            p: 1.5,
                                                            borderRadius: '12px',
                                                            background: rgba('#FFFFFF', 0.6),
                                                            backdropFilter: 'blur(8px)',
                                                            border: `1px solid ${palette.hairlineSoft}`,
                                                            display: 'flex',
                                                            flexWrap: 'wrap',
                                                            gap: 0.5,
                                                            maxHeight: 120,
                                                            overflow: 'auto',
                                                        }}
                                                    >
                                                        {['😊','😂','🤣','❤️','💕','🔥','✨','⭐','👍','👎','👏','🙏','💪','🤝','👋','🙌','🤗','🤔','😄','😅','😆','😉','😍','🥰','😘','🎉','🎊','🎁','🎈','🎀','🎂','🍾','🥂','🎵','🎶'].map((emoji) => (
                                                            <Button
                                                                key={emoji}
                                                                size="small"
                                                                onClick={() => {
                                                                    setNewMessage(prev => prev + emoji);
                                                                    setShowEmojiPicker(false);
                                                                }}
                                                                sx={{
                                                                    minWidth: 32,
                                                                    height: 32,
                                                                    p: 0,
                                                                    fontSize: '1.2rem',
                                                                    borderRadius: '8px',
                                                                }}
                                                            >
                                                                {emoji}
                                                            </Button>
                                                        ))}
                                                    </Box>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>

                                        <AnimatePresence mode="wait">
                                            {showQuickReplies && (
                                                <motion.div
                                                    key="quick-replies"
                                                    initial={{ opacity: 0, height: 0 }}
                                                    animate={{ opacity: 1, height: 'auto' }}
                                                    exit={{ opacity: 0, height: 0 }}
                                                >
                                                    <Box
                                                        sx={{
                                                            mt: 1.5,
                                                            p: 1.5,
                                                            borderRadius: '12px',
                                                            background: rgba('#FFFFFF', 0.6),
                                                            backdropFilter: 'blur(8px)',
                                                            border: `1px solid ${palette.hairlineSoft}`,
                                                            display: 'flex',
                                                            flexWrap: 'wrap',
                                                            gap: 1,
                                                        }}
                                                    >
                                                        {[
                                                            'Thank you for your message. I\'ll look into this right away.',
                                                            'I understand your concern. Let me check that for you.',
                                                            'Could you please provide more details about the issue?',
                                                            'I\'m happy to help! Let me investigate this.',
                                                            'Our team is working on this. I\'ll update you soon.',
                                                            'I\'ve escalated this to our technical team.',
                                                            'Can you share a screenshot of the issue?',
                                                            'I\'ve resolved this issue. Please confirm it\'s working.',
                                                        ].map((reply, idx) => (
                                                            <Chip
                                                                key={`reply-${idx}`}
                                                                label={reply}
                                                                onClick={() => {
                                                                    setNewMessage(reply);
                                                                    setShowQuickReplies(false);
                                                                }}
                                                                sx={{
                                                                    borderRadius: '10px',
                                                                    border: `1px solid ${palette.hairlineSoft}`,
                                                                    '&:hover': {
                                                                        borderColor: palette.plum,
                                                                        bgcolor: rgba(palette.plum, 0.05),
                                                                        transform: 'translateY(-2px)',
                                                                    },
                                                                    maxWidth: '100%',
                                                                    '& .MuiChip-label': {
                                                                        whiteSpace: 'normal',
                                                                        fontSize: '0.7rem',
                                                                    },
                                                                }}
                                                            />
                                                        ))}
                                                    </Box>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </Box>
                                </>
                            )}
                        </Box>
                    </Box>
                </GlassPanel>
            </Container>

            {/* ── Dialogs ── */}

            {/* Status Dialog */}
            <Dialog
                open={showStatusDialog}
                onClose={() => setShowStatusDialog(false)}
                maxWidth="xs"
                fullWidth
                TransitionComponent={Fade}
                slotProps={{ paper: {
                    sx: {
                        ...liquid(palette.indigo, { radius: 28 }),
                        background: `linear-gradient(160deg, ${rgba('#FFFFFF', 0.95)}, ${rgba(palette.canvas, 0.92)} 62%, ${rgba(palette.indigo, 0.1)})`,
                    },
                } }}
            >
                <Box
                    sx={{
                        p: 2.75,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: `1px solid ${palette.hairlineSoft}`,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
                        <GlassIcon icon={<CheckIcon />} color={palette.indigo} size={46} />
                        <Box>
                            <Typography
                                sx={{
                                    fontFamily: fontDisplay,
                                    fontWeight: 600,
                                    fontSize: '1.2rem',
                                    color: palette.ink,
                                }}
                            >
                                Update Status
                            </Typography>
                            <Typography sx={{ color: palette.inkLight, fontSize: '0.78rem' }}>
                                Change ticket status and priority
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton
                        onClick={() => setShowStatusDialog(false)}
                        sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '12px',
                            color: palette.inkSoft,
                            bgcolor: rgba(palette.ink, 0.04),
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>
                <DialogContent sx={{ p: 2.75, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <FormControl fullWidth size="small" sx={glassField(palette.indigo)}>
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={selectedStatus}
                            onChange={(e) => setSelectedStatus(e.target.value)}
                            label="Status"
                        >
                            <MuiMenuItem value="open">Open</MuiMenuItem>
                            <MuiMenuItem value="in_progress">In Progress</MuiMenuItem>
                            <MuiMenuItem value="resolved">Resolved</MuiMenuItem>
                            <MuiMenuItem value="closed">Closed</MuiMenuItem>
                        </Select>
                    </FormControl>
                    <FormControl fullWidth size="small" sx={glassField(palette.indigo)}>
                        <InputLabel>Priority</InputLabel>
                        <Select
                            value={selectedPriority || activeTicket?.priority || 'normal'}
                            onChange={(e) => setSelectedPriority(e.target.value)}
                            label="Priority"
                        >
                            <MuiMenuItem value="urgent">Urgent</MuiMenuItem>
                            <MuiMenuItem value="high">High</MuiMenuItem>
                            <MuiMenuItem value="normal">Normal</MuiMenuItem>
                            <MuiMenuItem value="low">Low</MuiMenuItem>
                        </Select>
                    </FormControl>
                </DialogContent>
                <Box
                    sx={{
                        px: 2.75,
                        py: 2,
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 1.25,
                        borderTop: `1px solid ${palette.hairlineSoft}`,
                    }}
                >
                    <Button
                        onClick={() => setShowStatusDialog(false)}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            color: palette.inkSoft,
                            borderRadius: '12px',
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disableElevation
                        onClick={() => {
                            if (activeTicket) {
                                updateTicketStatus(activeTicket.id, selectedStatus);
                                if (selectedPriority) {
                                    updateDoc(doc(db, 'supportTickets', activeTicket.id), {
                                        priority: selectedPriority,
                                    }).catch(console.error);
                                }
                            }
                        }}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 700,
                            borderRadius: '14px',
                            px: 2.75,
                            py: 1,
                            color: '#fff',
                            background: `linear-gradient(140deg, ${palette.indigo}, ${rgba(palette.indigo, 0.72)})`,
                            boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.28)}, 0 12px 28px -12px ${rgba(palette.indigo, 0.95)}`,
                        }}
                    >
                        Update
                    </Button>
                </Box>
            </Dialog>

            {/* Assign Dialog */}
            <Dialog
                open={showAssignDialog}
                onClose={() => setShowAssignDialog(false)}
                maxWidth="xs"
                fullWidth
                TransitionComponent={Fade}
                slotProps={{ paper: {
                    sx: {
                        ...liquid(palette.plum, { radius: 28 }),
                        background: `linear-gradient(160deg, ${rgba('#FFFFFF', 0.95)}, ${rgba(palette.canvas, 0.92)} 62%, ${rgba(palette.plum, 0.1)})`,
                    },
                } }}
            >
                <Box
                    sx={{
                        p: 2.75,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: `1px solid ${palette.hairlineSoft}`,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
                        <GlassIcon icon={<PersonIcon />} color={palette.plum} size={46} />
                        <Box>
                            <Typography
                                sx={{
                                    fontFamily: fontDisplay,
                                    fontWeight: 600,
                                    fontSize: '1.2rem',
                                    color: palette.ink,
                                }}
                            >
                                Assign Ticket
                            </Typography>
                            <Typography sx={{ color: palette.inkLight, fontSize: '0.78rem' }}>
                                Assign to a support agent
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton
                        onClick={() => setShowAssignDialog(false)}
                        sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '12px',
                            color: palette.inkSoft,
                            bgcolor: rgba(palette.ink, 0.04),
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>
                <DialogContent sx={{ p: 2.75 }}>
                    <FormControl fullWidth size="small" sx={glassField(palette.plum)}>
                        <InputLabel>Assign to</InputLabel>
                        <Select
                            value={assignedAgent}
                            onChange={(e) => setAssignedAgent(e.target.value)}
                            label="Assign to"
                        >
                            {availableAgents.map((agent) => (
                                <MuiMenuItem key={agent.id} value={agent.name || agent.displayName || agent.email}>
                                    {agent.name || agent.displayName || agent.email}
                                    {agent.online && ' 🟢'}
                                </MuiMenuItem>
                            ))}
                            <MuiMenuItem value="Unassigned">Unassigned</MuiMenuItem>
                        </Select>
                    </FormControl>
                </DialogContent>
                <Box
                    sx={{
                        px: 2.75,
                        py: 2,
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 1.25,
                        borderTop: `1px solid ${palette.hairlineSoft}`,
                    }}
                >
                    <Button
                        onClick={() => setShowAssignDialog(false)}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            color: palette.inkSoft,
                            borderRadius: '12px',
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disableElevation
                        onClick={() => {
                            if (activeTicket && assignedAgent) {
                                assignTicket(activeTicket.id, assignedAgent);
                            }
                        }}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 700,
                            borderRadius: '14px',
                            px: 2.75,
                            py: 1,
                            color: '#fff',
                            background: `linear-gradient(140deg, ${palette.plum}, ${rgba(palette.plum, 0.72)})`,
                            boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.28)}, 0 12px 28px -12px ${rgba(palette.plum, 0.95)}`,
                        }}
                    >
                        Assign
                    </Button>
                </Box>
            </Dialog>

            {/* Ticket Details Dialog */}
            <Dialog
                open={showTicketDetails}
                onClose={() => setShowTicketDetails(false)}
                maxWidth="sm"
                fullWidth
                TransitionComponent={Fade}
                slotProps={{ paper: {
                    sx: {
                        ...liquid(palette.teal, { radius: 28 }),
                        background: `linear-gradient(160deg, ${rgba('#FFFFFF', 0.95)}, ${rgba(palette.canvas, 0.92)} 62%, ${rgba(palette.teal, 0.1)})`,
                    },
                } }}
            >
                {activeTicket && (
                    <>
                        <Box
                            sx={{
                                p: 2.75,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                borderBottom: `1px solid ${palette.hairlineSoft}`,
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
                                <GlassIcon icon={<InfoIcon />} color={palette.teal} size={46} />
                                <Box>
                                    <Typography
                                        sx={{
                                            fontFamily: fontDisplay,
                                            fontWeight: 600,
                                            fontSize: '1.2rem',
                                            color: palette.ink,
                                        }}
                                    >
                                        Ticket Details
                                    </Typography>
                                    <Typography sx={{ color: palette.inkLight, fontSize: '0.78rem' }}>
                                        {activeTicket.subject || 'Support Request'}
                                    </Typography>
                                </Box>
                            </Box>
                            <IconButton
                                onClick={() => setShowTicketDetails(false)}
                                sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: '12px',
                                    color: palette.inkSoft,
                                    bgcolor: rgba(palette.ink, 0.04),
                                }}
                            >
                                <CloseIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Box>
                        <DialogContent sx={{ p: 2.75 }}>
                            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: '1fr 1fr' }}>
                                <Box>
                                    <Typography
                                        sx={{
                                            fontFamily: fontMono,
                                            fontSize: '0.58rem',
                                            letterSpacing: '0.1em',
                                            textTransform: 'uppercase',
                                            color: palette.inkFaint,
                                        }}
                                    >
                                        Status
                                    </Typography>
                                    <MetaPill
                                        label={getStatusLabel(activeTicket.status)}
                                        tone={getStatusColor(activeTicket.status)}
                                    />
                                </Box>
                                <Box>
                                    <Typography
                                        sx={{
                                            fontFamily: fontMono,
                                            fontSize: '0.58rem',
                                            letterSpacing: '0.1em',
                                            textTransform: 'uppercase',
                                            color: palette.inkFaint,
                                        }}
                                    >
                                        Priority
                                    </Typography>
                                    <MetaPill
                                        label={activeTicket.priority || 'Normal'}
                                        tone={activeTicket.priority === 'urgent' ? palette.danger : activeTicket.priority === 'high' ? palette.warning : palette.teal}
                                    />
                                </Box>
                                <Box>
                                    <Typography
                                        sx={{
                                            fontFamily: fontMono,
                                            fontSize: '0.58rem',
                                            letterSpacing: '0.1em',
                                            textTransform: 'uppercase',
                                            color: palette.inkFaint,
                                        }}
                                    >
                                        Category
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: palette.ink }}>
                                        {activeTicket.category || 'General'}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography
                                        sx={{
                                            fontFamily: fontMono,
                                            fontSize: '0.58rem',
                                            letterSpacing: '0.1em',
                                            textTransform: 'uppercase',
                                            color: palette.inkFaint,
                                        }}
                                    >
                                        Company
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: palette.ink }}>
                                        {activeTicket.companyName || 'N/A'}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography
                                        sx={{
                                            fontFamily: fontMono,
                                            fontSize: '0.58rem',
                                            letterSpacing: '0.1em',
                                            textTransform: 'uppercase',
                                            color: palette.inkFaint,
                                        }}
                                    >
                                        Created
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: palette.ink }}>
                                        {formatFullTime(activeTicket.createdAt)}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography
                                        sx={{
                                            fontFamily: fontMono,
                                            fontSize: '0.58rem',
                                            letterSpacing: '0.1em',
                                            textTransform: 'uppercase',
                                            color: palette.inkFaint,
                                        }}
                                    >
                                        Updated
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: palette.ink }}>
                                        {formatFullTime(activeTicket.updatedAt)}
                                    </Typography>
                                </Box>
                                {activeTicket.assignedTo && (
                                    <Box sx={{ gridColumn: '1 / -1' }}>
                                        <Typography
                                            sx={{
                                                fontFamily: fontMono,
                                                fontSize: '0.58rem',
                                                letterSpacing: '0.1em',
                                                textTransform: 'uppercase',
                                                color: palette.inkFaint,
                                            }}
                                        >
                                            Assigned To
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: palette.ink }}>
                                            {activeTicket.assignedTo}
                                        </Typography>
                                    </Box>
                                )}
                                {activeTicket.rating && (
                                    <Box sx={{ gridColumn: '1 / -1' }}>
                                        <Typography
                                            sx={{
                                                fontFamily: fontMono,
                                                fontSize: '0.58rem',
                                                letterSpacing: '0.1em',
                                                textTransform: 'uppercase',
                                                color: palette.inkFaint,
                                            }}
                                        >
                                            Rating
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                            <Rating value={activeTicket.rating} readOnly size="small" />
                                            {activeTicket.ratingFeedback && (
                                                <Typography
                                                    variant="caption"
                                                    sx={{ color: palette.inkSoft, fontStyle: 'italic' }}
                                                >
                                                    &quot;{activeTicket.ratingFeedback}&quot;
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        </DialogContent>
                        <Box
                            sx={{
                                px: 2.75,
                                py: 2,
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: 1.25,
                                borderTop: `1px solid ${palette.hairlineSoft}`,
                            }}
                        >
                            <Button
                                onClick={() => setShowTicketDetails(false)}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    color: palette.inkSoft,
                                    borderRadius: '12px',
                                }}
                            >
                                Close
                            </Button>
                            <Button
                                variant="contained"
                                disableElevation
                                onClick={() => { setShowTicketDetails(false); setShowAssignDialog(true); }}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    borderRadius: '14px',
                                    px: 2.75,
                                    py: 1,
                                    color: '#fff',
                                    background: `linear-gradient(140deg, ${palette.plum}, ${rgba(palette.plum, 0.72)})`,
                                    boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.28)}, 0 12px 28px -12px ${rgba(palette.plum, 0.95)}`,
                                }}
                            >
                                Assign
                            </Button>
                        </Box>
                    </>
                )}
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={5000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    sx={{
                        borderRadius: '12px',
                        ...liquid(
                            snackbar.severity === 'error'
                                ? palette.danger
                                : snackbar.severity === 'warning'
                                ? palette.warning
                                : palette.teal
                        ),
                        '& .MuiAlert-icon': { color: '#fff' },
                    }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            <style>{`
                @keyframes tfPulse {
                    0%, 100% { opacity: 1; transform: scale(1); }
                    50%      { opacity: .45; transform: scale(.82); }
                }
                @keyframes tfSpin { to { transform: rotate(360deg); } }
                @keyframes tfGrain {
                    0%   { transform: translate(0, 0); }
                    25%  { transform: translate(-2%, 1%); }
                    50%  { transform: translate(1%, -2%); }
                    75%  { transform: translate(-1%, -1%); }
                    100% { transform: translate(0, 0); }
                }
                * { scrollbar-width: thin; scrollbar-color: ${rgba(palette.ink, 0.16)} transparent; }
                *::-webkit-scrollbar { width: 6px; height: 6px; }
                *::-webkit-scrollbar-track { background: transparent; }
                *::-webkit-scrollbar-thumb { background: ${rgba(palette.ink, 0.14)}; border-radius: 999px; }
                *::-webkit-scrollbar-thumb:hover { background: ${rgba(palette.ink, 0.26)}; }
                @media (prefers-reduced-motion: reduce) {
                    *, *::before, *::after {
                        animation-duration: .001ms !important;
                        animation-iteration-count: 1 !important;
                        transition-duration: .001ms !important;
                    }
                }
            `}</style>
        </Box>
    );
};

export default AdminSupport;